import {
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { AppState } from 'src/app/reducers';
import { Language } from '../../models/core.models';
import { NomSupportedLanguages } from '../../constants';
import { distinctUntilChanged, map, Observable, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { injectSetUserLanguageMutation } from 'src/app/api/queries/user-api.queries';
import { selectCoreLanguage } from '../../reducers';
import {
  setCoreLanguage,
  setCoreLanguageSuccess,
} from '../../actions/core.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-language-picker',
  templateUrl: './language-picker.component.html',
  styleUrls: ['./language-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LanguagePickerComponent {
  currentLanguage$: Observable<Language>;
  currentLanguage: string = this.translateService.currentLang;
  languages = NomSupportedLanguages;
  protected destroyRef = inject(DestroyRef);
  private readonly setUserLanguageMutation = injectSetUserLanguageMutation();
  constructor(
    private translateService: TranslateService,
    private store: Store<AppState>,
  ) {
    this.currentLanguage$ = this.store.select(selectCoreLanguage);
    this.currentLanguage$
      .pipe(
        // extract just the code string
        map((language) => language.code),

        // only update when the code actually changes
        distinctUntilChanged(),

        // side‑effect: assign to the component property
        tap((code) => {
          this.currentLanguage = code;
          console.log('Language changed to:', code);
        }),

        // auto‑unsubscribe on component destroy
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleLanguageChange(selectedCode: string) {
    const selectedLanguage = this.languages.find(
      (lang) => lang.code === selectedCode,
    );
    if (selectedLanguage) {
      this.currentLanguage = selectedLanguage.code;
      // `setCoreLanguage` is the client-state write; `setCoreLanguageSuccess` is
      // broadcast after the save lands, and is the cue `HelpContentService` clears
      // its cache on.
      this.store.dispatch(setCoreLanguage({ language: selectedLanguage }));
      this.setUserLanguageMutation
        .mutateAsync(selectedLanguage.id)
        .then(() => this.store.dispatch(setCoreLanguageSuccess()))
        .catch(() => {});
    }
  }

  getFlagEmoji(code: string): string {
    const flags: Record<string, string> = {
      en: '🇬🇧', // English - United Kingdom
      bg: '🇧🇬', // Bulgarian - Bulgaria
      ar: '🇸🇦', // Arabic - Saudi Arabia
    };
    return flags[code] || '🏳️'; // fallback flag (white flag)
  }
}
